import { Link } from 'react-router';
import { Building2, CalendarClock, MapPin, Settings2, ShieldAlert, ShieldCheck } from 'lucide-react';
import type { Emisor } from '@/shared/api/tipos';

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return dia && mes && anio ? `${dia}/${mes}/${anio}` : iso;
}

export function Identidad({ emisor }: { emisor: Emisor }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Empresa emisor={emisor} />
      <Firma emisor={emisor} />
    </div>
  );
}

function Empresa({ emisor }: { emisor: Emisor }) {
  return (
    <article className="rounded-2xl border border-armazon-100 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-4">
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-blue-50 text-blue-600">
          <Building2 className="size-5" aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-bold uppercase tracking-tight text-armazon-900">
            {emisor.razonSocial}
          </h2>
          {emisor.nombreComercial && (
            <p className="truncate text-xs text-armazon-500">{emisor.nombreComercial}</p>
          )}

          <dl className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-armazon-600">
            <div className="flex items-center gap-1.5">
              <dt className="text-armazon-400">RUC</dt>
              <dd className="font-medium tabular-nums">{emisor.ruc}</dd>
            </div>
            <div className="flex min-w-0 items-center gap-1.5">
              <MapPin className="size-3.5 shrink-0 text-armazon-400" aria-hidden />
              <dd className="truncate" title={emisor.direccionMatriz}>
                {emisor.direccionMatriz}
              </dd>
            </div>
          </dl>

          <span
            className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
              emisor.esProduccion
                ? 'bg-marca-50 text-marca-700'
                : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
            }`}
          >
            <span
              className={`size-1.5 rounded-full ${
                emisor.esProduccion ? 'bg-marca-500' : 'bg-amber-500'
              }`}
              aria-hidden
            />
            {emisor.esProduccion ? 'Producción' : 'Pruebas'}
          </span>
        </div>

        <Link
          to="/configuracion"
          title="Editar los datos del emisor"
          className="shrink-0 rounded-lg p-1.5 text-armazon-400 transition hover:bg-armazon-50 hover:text-armazon-700"
        >
          <Settings2 className="size-4" aria-hidden />
          <span className="sr-only">Editar los datos del emisor</span>
        </Link>
      </div>
    </article>
  );
}

function Firma({ emisor }: { emisor: Emisor }) {
  const certificado = emisor.certificado;

  const estado = !certificado
    ? ('sin' as const)
    : !certificado.vigente
      ? ('caducado' as const)
      : certificado.porCaducar
        ? ('porCaducar' as const)
        : ('vigente' as const);

  const TONO = {
    sin: { caja: 'bg-armazon-100 text-armazon-500', pastilla: 'bg-armazon-100 text-armazon-600' },
    caducado: { caja: 'bg-red-50 text-red-600', pastilla: 'bg-red-100 text-red-700' },
    porCaducar: { caja: 'bg-amber-50 text-amber-600', pastilla: 'bg-amber-100 text-amber-700' },
    vigente: { caja: 'bg-marca-50 text-marca-600', pastilla: 'bg-marca-100 text-marca-700' },
  }[estado];

  const ROTULO = {
    sin: 'Sin cargar',
    caducado: 'Caducada',
    porCaducar: 'Por caducar',
    vigente: 'Vigente',
  }[estado];

  const Icono = estado === 'vigente' ? ShieldCheck : ShieldAlert;

  return (
    <article className="rounded-2xl border border-armazon-100 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-4">
        <span className={`grid size-11 shrink-0 place-items-center rounded-xl ${TONO.caja}`}>
          <Icono className="size-5" aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-sm font-bold uppercase tracking-tight text-armazon-900">
              Firma electrónica
            </h2>
            <span
              className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${TONO.pastilla}`}
            >
              {ROTULO}
            </span>
          </div>

          {certificado ? (
            <>
              <p className="mt-1 truncate text-xs text-armazon-500" title={certificado.titular ?? ''}>
                {certificado.titular ?? 'Sin titular en el certificado'}
              </p>

              <dl className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-armazon-600">
                <div className="flex items-center gap-1.5">
                  <CalendarClock className="size-3.5 text-armazon-400" aria-hidden />
                  <dt className="text-armazon-400">Vence</dt>
                  <dd className="font-medium tabular-nums">
                    {enFecha(certificado.validoHasta)}
                  </dd>
                </div>

                <div className="flex items-center gap-1.5">
                  <dd
                    className={`font-medium ${
                      certificado.vigente ? 'text-armazon-600' : 'text-red-700'
                    }`}
                  >
                    {certificado.vigente
                      ? `Quedan ${certificado.diasRestantes.toLocaleString('es-EC')} días`
                      : 'Ya no sirve para emitir'}
                  </dd>
                </div>
              </dl>
            </>
          ) : (
            <p className="mt-2 text-xs text-armazon-500">
              Sin certificado cargado: el sistema no puede firmar comprobantes.
            </p>
          )}

          {estado !== 'vigente' && (
            <Link
              to="/configuracion"
              className="mt-3 inline-block text-xs font-semibold text-marca-700 underline-offset-2 hover:underline"
            >
              Cargar o renovar el certificado
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
